import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  FaArrowRight,
  FaBolt,
  FaChevronDown,
  FaClock,
  FaGift,
  FaMagic,
  FaMapMarkerAlt,
  FaPlus,
  FaShoppingBag,
  FaTag,
} from "react-icons/fa";

import { getCurrentUser } from "./session";
import SignInModal from "./SignInModal";

import "./HubHappening.css";

/* =========================================================
   HubHappening   "Local deals & finds"

   Offers from local businesses plus free finds on HubMart, in ONE
   section with one filter row: All · Deals · Free · New this week.
   Events are not here any more   they live in HubEventsRail
   ("Upcoming events"), so nothing on the hub is shown twice.

   • Tapping a card opens the detail modal below (card-first).
   • "Post a deal" goes to /post-offer behind the sign-in check:
     the gold card at the end of the phone rail, or the pill in the
     section header on desktop (CSS shows one or the other).
   • Desktop: one grid row + "Show N more". Phones: swipe rail.

   The section header uses the shared .hub-sechead styles from
   Hub.css. Place on the hub with:  <HubHappening />
   ========================================================= */

const BUSINESS_API = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const HUBMART_API = "https://api.goourhub.com:435";

const HUBMART_IMAGE_BASE = "https://goourhub.sfo2.digitaloceanspaces.com/img/";

const SAN_ANTONIO_TIMEZONE = "America/Chicago";

const MAX_ITEMS = 16;

const NEW_WINDOW_DAYS = 7;

const FILTERS = [
  { key: "all", label: "All", icon: FaBolt },
  { key: "deals", label: "Deals", icon: FaTag },
  { key: "free", label: "Free", icon: FaGift },
  { key: "new", label: "New this week", icon: FaMagic },
];

const KIND_LABELS = {
  deal: "Deal",
  free: "Free on HubMart",
};

const KIND_ICONS = {
  deal: FaTag,
  free: FaShoppingBag,
};

/* ---------------------------------------------------------
   Small helpers (unchanged behavior from the previous version)
   --------------------------------------------------------- */

function pickArray(data, keys = []) {
  const candidates = [
    data,
    ...keys.map((key) => data?.[key]),
    data?.data,
    data?.data?.results,
    data?.data?.items,
    data?.data?.offers,
  ];

  return candidates.find((candidate) => Array.isArray(candidate)) || [];
}

function getObjectId(value) {
  if (!value) return "";

  if (typeof value === "object") {
    return String(value.$oid || value.oid || value.id || value._id || "");
  }

  return String(value);
}

function getHubMartItemId(item) {
  const raw =
    item?.postid ||
    item?.postId ||
    item?.adid ||
    item?.adId ||
    item?.recordId ||
    item?._id ||
    item?.id ||
    item?.ID ||
    "";

  const normalized = getObjectId(raw);

  if (normalized) return normalized;

  if (item?.title) {
    return String(item.title)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .slice(0, 48);
  }

  return "";
}

async function fetchJSON(url, signal) {
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.message || `Request failed (${response.status})`);
  }

  return data;
}

function firstImage(value) {
  if (!value) return "";

  if (Array.isArray(value)) return firstImage(value[0]);

  if (typeof value === "object") {
    return firstImage(value.files || value.images || Object.values(value));
  }

  const text = String(value).trim();

  if (!text) return "";

  if (text.startsWith("[") || text.startsWith("{")) {
    try {
      return firstImage(JSON.parse(text));
    } catch {
      // plain text below
    }
  }

  return text.split(",")[0]?.trim() || "";
}

function businessImageURL(value) {
  const image = firstImage(value);

  if (!image) return "";
  if (/^(https?:|data:|blob:)/i.test(image)) return image;
  if (image.startsWith("/")) return BUSINESS_API + image;

  return image;
}

function hubMartImageURL(value) {
  const image = firstImage(value);

  if (!image) return "";
  if (/^(https?:|data:|blob:)/i.test(image)) return image;

  return HUBMART_IMAGE_BASE + image.replace(/^\/+/, "");
}

function zoneDateParts() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: SAN_ANTONIO_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
  };
}

function toDateString(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date, number) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + number);
  return next;
}

function getDateContext() {
  const parts = zoneDateParts();
  const todayDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));

  return {
    today: toDateString(todayDate),
    since: toDateString(addDays(todayDate, -NEW_WINDOW_DAYS)),
  };
}

function formatDate(value) {
  if (!value) return "";

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) return value;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(year, month - 1, day));
}

/* ---------------------------------------------------------
   Normalizers: both sources become the same card shape.
   --------------------------------------------------------- */

function normalizeOffer(offer) {
  const id =
    getObjectId(offer.id || offer._id) || `${offer.title}-${offer.business_name}`;

  return {
    key: `deal-${id}`,
    kind: "deal",
    title: offer.business_name || offer.title || "Local business",
    subtitle: offer.business_name ? offer.title || "Special offer" : "Special offer",
    description: offer.short_description || "",
    badge: offer.offer_text || "Deal",
    image: businessImageURL(offer.image_url),
    dateStart: offer.start_date || String(offer.created_at || "").slice(0, 10),
    dateEnd: offer.end_date || "",
    meta: offer.end_date ? `Ends ${formatDate(offer.end_date)}` : "Limited time",
    secondary: offer.promo_phrase ? `Use: ${offer.promo_phrase}` : "",
    place: offer.city || offer.location || "San Antonio",
    free: false,
    href: "",
    actionLabel: "",
  };
}

function normalizeFreeItem(item) {
  const id = getHubMartItemId(item);

  const image = hubMartImageURL(
    item.images || item.files || item.image || item.image1 || item.file1
  );

  /* HubMart's detail page expects the full listing to already be
     cached when opening #/item/:id from another page. */
  const marketplaceItem = {
    ...item,
    category: item.category || "free",
    categoryId: item.categoryId != null ? item.categoryId : 0,
    price: item.price != null ? item.price : 0,
    location: item.location || "San Antonio, TX",
    image: item.image || image,
    images: Array.isArray(item.images) ? item.images : image ? [image] : [],
  };

  return {
    key: `free-${id || item.title}`,
    kind: "free",
    title: item.title || "Free local item",
    subtitle: item.description
      ? String(item.description).slice(0, 80)
      : "Free · pick up locally",
    description: item.description || "Available locally in San Antonio.",
    badge: "FREE",
    image,
    dateStart: String(item.created_at || item.createdAt || "").slice(0, 10),
    dateEnd: "",
    meta: "Free on HubMart",
    secondary: item.location || "San Antonio, TX",
    place: item.location || "San Antonio",
    free: true,
    marketplaceId: id,
    marketplaceItem,
    href: id ? `/HubMart#/item/${encodeURIComponent(id)}` : "/HubMart",
    actionLabel: "View item",
  };
}

function uniqueItems(items) {
  const seen = new Set();

  return items.filter((item) => {
    if (!item?.key || seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
}

/* Cards in one desktop grid row; 0 means "never collapse"
   (the phone rail shows everything). Mirrors HubHappening.css:
   3 columns, 2 below 1050px, a swipe rail below 680px. */
function getCardsPerRow() {
  if (typeof window === "undefined") return 3;
  if (window.matchMedia("(max-width: 680px)").matches) return 0;
  if (window.matchMedia("(max-width: 1050px)").matches) return 2;
  return 3;
}

/* ---------------------------------------------------------
   Pieces
   --------------------------------------------------------- */

function HappeningAction({ item }) {
  if (!item.href || !item.actionLabel) return null;

  const content = (
    <>
      {item.actionLabel}
      <FaArrowRight aria-hidden="true" />
    </>
  );

  /* HubMart detail pages depend on the listing being cached before
     the hash route opens (matches HubMart's openListingInSameTab). */
  if (item.kind === "free" && item.marketplaceId && item.marketplaceItem) {
    return (
      <Link
        className="hub-happening-card__action"
        to={item.href}
        onClick={() => {
          try {
            sessionStorage.setItem(
              `hb:item:${item.marketplaceId}`,
              JSON.stringify(item.marketplaceItem)
            );
          } catch {
            // the user can still reach HubMart itself
          }
        }}
      >
        {content}
      </Link>
    );
  }

  if (/^https?:\/\//i.test(item.href)) {
    return (
      <a
        className="hub-happening-card__action"
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    );
  }

  return (
    <Link className="hub-happening-card__action" to={item.href}>
      {content}
    </Link>
  );
}

function HappeningCard({ item, onOpen }) {
  const Icon = KIND_ICONS[item.kind] || FaBolt;

  const openCard = () => onOpen(item);

  return (
    <article
      className={`hub-happening-card hub-happening-card--${item.kind}`}
      role="button"
      tabIndex={0}
      onClick={openCard}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openCard();
        }
      }}
    >
      <span className="hub-happening-card__badge">{item.badge}</span>

      <div className="hub-happening-card__body">
        <h3>{item.title}</h3>

        <p className="hub-happening-card__sub">{item.subtitle}</p>

        <span className="hub-happening-card__place">
          <FaMapMarkerAlt aria-hidden="true" />
          {item.place || item.secondary || "San Antonio"}
        </span>
      </div>

      <div className="hub-happening-card__media" aria-hidden="true">
        {item.image ? (
          <img src={item.image} alt="" loading="lazy" />
        ) : (
          <div className="hub-happening-card__placeholder">
            <Icon />
          </div>
        )}
      </div>
    </article>
  );
}

/* Deals + free finds open this. Events open EventDetailsSheet. */
function HappeningDetailModal({ item, onClose }) {
  useEffect(() => {
    if (!item) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [item, onClose]);

  if (!item) return null;

  const Icon = KIND_ICONS[item.kind] || FaBolt;

  const content = (
    <div
      className="hub-happening-modal"
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="hub-happening-modal__panel">
        <button
          type="button"
          className="hub-happening-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="hub-happening-modal__media">
          {item.image ? (
            <img src={item.image} alt={item.title} />
          ) : (
            <div className="hub-happening-modal__placeholder">
              <Icon aria-hidden="true" />
              <span>{KIND_LABELS[item.kind]}</span>
            </div>
          )}
        </div>

        <div className="hub-happening-modal__content">
          <div className="hub-happening-modal__topline">
            <span>{KIND_LABELS[item.kind]}</span>
            <strong>{item.badge}</strong>
          </div>

          <h2>{item.title}</h2>
          <h3>{item.subtitle}</h3>

          {item.description && (
            <p className="hub-happening-modal__description">{item.description}</p>
          )}

          <div className="hub-happening-modal__details">
            {item.meta && (
              <div>
                <FaClock aria-hidden="true" />
                <span>{item.meta}</span>
              </div>
            )}

            {item.secondary && (
              <div>
                <FaMapMarkerAlt aria-hidden="true" />
                <span>{item.secondary}</span>
              </div>
            )}
          </div>

          <div className="hub-happening-modal__actions">
            <HappeningAction item={item} />

            <button
              type="button"
              className="hub-happening-modal__done"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

/* ---------------------------------------------------------
   Section
   --------------------------------------------------------- */

export default function HubHappening() {
  const navigate = useNavigate();

  const [filter, setFilter] = useState("all");

  const [offers, setOffers] = useState([]);
  const [freeItems, setFreeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const openFromHubSearch = (event) => {
      const selected = event?.detail?.item;
      if (selected && typeof selected === "object") setSelectedItem(selected);
    };

    window.addEventListener("gohub:open-happening", openFromHubSearch);
    return () =>
      window.removeEventListener("gohub:open-happening", openFromHubSearch);
  }, []);
  const [signInOpen, setSignInOpen] = useState(false);

  /* Desktop: one row + "Show more". Phones never collapse (perRow 0). */
  const [expanded, setExpanded] = useState(false);
  const [perRow, setPerRow] = useState(() => getCardsPerRow());

  useEffect(() => {
    const queries = [
      window.matchMedia("(max-width: 680px)"),
      window.matchMedia("(max-width: 1050px)"),
    ];

    const onChange = () => setPerRow(getCardsPerRow());

    queries.forEach((query) => {
      if (query.addEventListener) query.addEventListener("change", onChange);
      else query.addListener(onChange);
    });

    return () => {
      queries.forEach((query) => {
        if (query.removeEventListener) {
          query.removeEventListener("change", onChange);
        } else {
          query.removeListener(onChange);
        }
      });
    };
  }, []);

  /* a new filter snaps the grid back to one row */
  useEffect(() => {
    setExpanded(false);
  }, [filter]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadFeed() {
      setLoading(true);
      setError("");

      const results = await Promise.allSettled([
        fetchJSON(`${BUSINESS_API}/api/offers?limit=16`, controller.signal),
        fetchJSON(
          `${HUBMART_API}/search?category=0&ct=1&limit=12`,
          controller.signal
        ),
      ]);

      if (controller.signal.aborted) return;

      const [offerResult, freeResult] = results;

      setOffers(
        offerResult.status === "fulfilled"
          ? pickArray(offerResult.value, ["offers"])
          : []
      );

      setFreeItems(
        freeResult.status === "fulfilled"
          ? pickArray(freeResult.value, ["results", "result", "items"])
          : []
      );

      if (results.every((result) => result.status === "rejected")) {
        setError("Deals and finds could not be loaded right now.");
      }

      setLoading(false);
    }

    loadFeed();

    return () => controller.abort();
  }, []);

  const normalized = useMemo(
    () => ({
      offers: offers
        .map(normalizeOffer)
        .sort((left, right) =>
          String(left.dateEnd).localeCompare(String(right.dateEnd))
        ),
      freeItems: freeItems.map(normalizeFreeItem),
    }),
    [offers, freeItems]
  );

  const allItems = useMemo(
    () => uniqueItems([...normalized.offers, ...normalized.freeItems]),
    [normalized]
  );

  const visibleItems = useMemo(() => {
    const { since } = getDateContext();

    switch (filter) {
      case "deals":
        return normalized.offers.slice(0, MAX_ITEMS);
      case "free":
        return normalized.freeItems.slice(0, MAX_ITEMS);
      case "new":
        return allItems
          .filter((item) => item.dateStart && item.dateStart >= since)
          .slice(0, MAX_ITEMS);
      default:
        return allItems.slice(0, MAX_ITEMS);
    }
  }, [filter, normalized, allItems]);

  const activeFilterLabel =
    FILTERS.find((item) => item.key === filter)?.label || "All";

  const handlePostDeal = () => {
    const current = getCurrentUser();

    if (current?.email && current?.token) {
      navigate("/post-offer");
      return;
    }

    setSignInOpen(true);
  };

  const collapsible = perRow > 0 && visibleItems.length > perRow;
  const shownItems =
    collapsible && !expanded ? visibleItems.slice(0, perRow) : visibleItems;
  const hiddenCount = visibleItems.length - shownItems.length;

  const postCard = (
    <button
      type="button"
      className="hub-happening-card hub-happening-card--cta"
      onClick={handlePostDeal}
    >
      <span className="hub-happening-cta__ic" aria-hidden="true">
        <FaPlus />
      </span>
      <span className="hub-happening-cta__t">
        Post
        <br />
        a deal
      </span>
      <FaArrowRight aria-hidden="true" />
    </button>
  );

  return (
    <>
      <section className="hub-happening" aria-labelledby="hub-happening-title">
        <div className="hub-sechead">
          <span className="hub-sechead__ic hub-sechead__ic--gold" aria-hidden="true">
            <FaTag />
          </span>

          <div className="hub-sechead__tx">
            <h2 id="hub-happening-title">
              Local deals &amp; finds
              {!loading && (
                <span className="hub-sechead__count">{allItems.length}</span>
              )}
            </h2>
            <p>Offers, free stuff, and new finds.</p>
          </div>

          {/* desktop pill; phones use the gold card in the rail instead */}
          <button
            type="button"
            className="hub-happening__post"
            onClick={handlePostDeal}
          >
            <FaPlus aria-hidden="true" />
            Post a deal
          </button>
        </div>

        <div className="hub-happening__filters" aria-label="Deal filters">
          {FILTERS.map((item) => {
            const Icon = item.icon;
            const active = filter === item.key;

            return (
              <button
                key={item.key}
                type="button"
                className={`hub-happening__filter ${active ? "is-active" : ""}`}
                aria-pressed={active}
                onClick={() => setFilter(item.key)}
              >
                <Icon aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="hub-happening__loading" role="status">
            Finding deals around San Antonio...
          </div>
        ) : error && visibleItems.length === 0 ? (
          <div className="hub-happening__empty">
            <strong>Couldn't load deals</strong>
            <p>{error}</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="hub-happening__rail-wrap">
            <div className="hub-happening__empty">
              <strong>
                {filter === "all"
                  ? "No deals listed yet"
                  : `Nothing under “${activeFilterLabel}” right now`}
              </strong>
              <p>Local businesses can post an offer free.</p>
            </div>
            {postCard}
          </div>
        ) : (
          <>
            <div className="hub-happening__grid">
              {shownItems.map((item) => (
                <HappeningCard key={item.key} item={item} onOpen={setSelectedItem} />
              ))}
              {postCard}
            </div>

            {collapsible && (
              <button
                type="button"
                className={`hub-happening__more ${expanded ? "is-open" : ""}`}
                aria-expanded={expanded}
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded ? "Show less" : `Show ${hiddenCount} more`}
                <FaChevronDown aria-hidden="true" />
              </button>
            )}
          </>
        )}
      </section>

      {signInOpen && (
        <SignInModal
          isOpen={signInOpen}
          onClose={() => setSignInOpen(false)}
          onSignInSuccess={() => {
            setSignInOpen(false);
            navigate("/post-offer");
          }}
        />
      )}

      <HappeningDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
    </>
  );
}
